import { ProcessFormTemplate } from 'types/dataCaptureTypes';
import { TFunction } from 'i18next';
import { ConfigurationTabs } from 'types/processBlockTypes';
import { FormConfigurationField, FieldTypeKeys } from './types';

export interface FieldContext {
  formTemplates: ProcessFormTemplate[];
  integrationOptions: { value: string; label: string }[];
  t: TFunction;
  settingsFieldMaxWidth: number;
  onDataSourceSelect?: (value: string) => void;
}

/**
 * Factory function to get form configuration fields based on capture mode
 * This centralizes field definitions and decouples them from rendering logic
 */
export function getFormConfigurationFields(
  mode: ConfigurationTabs,
  context: FieldContext
): FormConfigurationField[] {
  switch (mode) {
    case ConfigurationTabs.FormCapture:
      return getFormCaptureFields(context);
    case ConfigurationTabs.FlatFile:
      return getFlatFileFields(context);
    default:
      return [];
  }
}

/**
 * Get fields for Form Capture mode
 */
function getFormCaptureFields(context: FieldContext): FormConfigurationField[] {
  const { formTemplates, t, settingsFieldMaxWidth } = context;

  return [
    {
      name: 'form',
      label: t('process-flow.data-capture.form'),
      placeholder: t('process-flow.data-capture.select-form'),
      style: { width: '100%', maxWidth: settingsFieldMaxWidth },
      options: formTemplates.map(template => ({
        value: template.form_id,
        label: template.form_name,
      })),
    },
    {
      name: 'formVersion',
      label: t('process-flow.data-capture.form-version'),
    },
    {
      name: 'userIds',
      label: t('process-flow.data-capture.users'),
      placeholder: t('process-flow.data-capture.select-user'),
      style: { width: '100%', maxWidth: settingsFieldMaxWidth },
    },
  ];
}

/**
 * Get fields for Flat File mode
 */
function getFlatFileFields(context: FieldContext): FormConfigurationField[] {
  const { integrationOptions, t, settingsFieldMaxWidth, onDataSourceSelect } =
    context;

  return [
    {
      name: 'formflatFileIntegration',
      label: t('process-flow.data-capture.form-initial-fields'),
      placeholder: t('process-flow.data-capture.select-initial-fields'),
      style: { width: '100%', maxWidth: settingsFieldMaxWidth },
      options: integrationOptions,
      onSelect: onDataSourceSelect,
    },
    {
      name: 'externalCode',
      label: t('process-flow.data-capture.external-code'),
    },
  ];
}

/**
 * Type guard to check if a field name requires special handling
 */
export function isSpecialField(fieldName: string): boolean {
  return Object.values(FieldTypeKeys).includes(fieldName as any);
}
